import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import api from '../api/client';
import { formatDateTime } from '../lib/format';
import { History, Loader2, ShieldCheck } from 'lucide-react';

/**
 * Read-only sign-in audit trail shown from the profile menu. Renders the last
 * 5 successful sign-ins for the current user so they can quickly spot an
 * unfamiliar IP / device and rotate their password if needed.
 */
const RecentSignInsDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get('/auth/login-history');
        if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load sign-in history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const shortUA = (ua) => {
    if (!ua) return '—';
    // Compact user-agent to something readable at a glance.
    const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/i);
    const os = /Windows/.test(ua) ? 'Windows'
      : /Mac OS X/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android'
      : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
      : /Linux/.test(ua) ? 'Linux'
      : 'Unknown OS';
    return m ? `${m[1]} on ${os}` : os;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="recent-signins-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[hsl(217,91%,24%)]">
            <History className="h-5 w-5" /> Recent sign-ins
          </DialogTitle>
          <DialogDescription>
            The last 5 successful sign-ins on your account. If you see anything you don&apos;t recognise, change your password immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2" data-testid="recent-signins-list">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 py-4">{error}</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              No sign-in history yet — you&apos;ll see entries here after your next login.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {items.map((it, i) => (
                <li key={it.id} className="p-3" data-testid={`recent-signin-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {formatDateTime(it.created_at)}
                        {i === 0 && (
                          <span className="ml-2 text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Most recent</span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-600 truncate" title={it.user_agent || ''}>
                        {shortUA(it.user_agent)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">IP</div>
                      <div className="text-xs font-mono text-slate-700">{it.ip || '—'}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pt-3 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="recent-signins-close-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecentSignInsDialog;
